import { Icon, Tooltip } from '@lightspeed/design-system-react'
import { PAYMENT_TYPES } from '@vend/business-components'
import { trackUserEvent } from '@vend/utilities'
import React, { useEffect } from 'react'
import { T, useT } from '@transifex/react'
import {
  useIsCalculatingSale,
  useRegisterManager,
} from '../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  selectOutstandingBalance,
  selectSaleLocalId,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { FEATURE_LSPAY_CP_CNP_SPLIT_ENTRY } from '../../../constants'
import {
  selectIsTransitioningToPaymentScreenViaLsPay,
  setIsTransitioningToPaymentScreenViaLsPay,
  selectIsSaleRecalculationLocked,
} from '../../../state/slices/uiStateSlice'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { WEB_REGISTER } from '../../web-register.constant'
import { getMinimumLsPayAmount } from '../../../util/lspay-utils'
import { requestTerminalStatusUpdate } from '../../../state/slices/terminalStatus/terminalStatusSlice'
import { LSPayCardPresentEntryIcon } from '../payment-panel/lspay-entry-icons'
import { useLSQuickPay } from './useLSQuickPay'
import { SalePaymentButton } from './sale-payment-button/sale-payment-button'
import { useTransitionToPaymentScreen } from './sale-payment-button/use-transition-to-payment-screen'

export const SalePayments = () => {
  const { showQuickPayButton, disableQuickPayButton } = useLSQuickPay()
  const transitionToPaymentScreen = useTransitionToPaymentScreen(true)
  const saleCalculationRunning = useIsCalculatingSale()
  const registerManager = useRegisterManager()
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const saleId = useAppSelector(selectSaleLocalId)
  const isTransitioningToPaymentScreenViaLsPay = useAppSelector(
    selectIsTransitioningToPaymentScreenViaLsPay
  )
  const dispatch = useAppDispatch()
  const t = useT()
  const isSaleRecalculationInProgress = useAppSelector(
    selectIsSaleRecalculationLocked
  )

  // Get currency-specific minimum amount from active outlet
  const { minimumAmount, currencySymbol } = getMinimumLsPayAmount(
    registerManager.getActiveOutlet()
  )

  const isAmountBelowMinimum =
    outstandingBalance <= 0 || outstandingBalance < minimumAmount
  // TODO: LSPAY-43773 — once lspay_cp_cnp_split_entry is fully rolled out and
  // stable in Prod, remove this flag check.
  const isLSPaySplitEntryEnabled = getFeatureFromCache(
    FEATURE_LSPAY_CP_CNP_SPLIT_ENTRY
  )

  const handleQuickPayClick = () => {
    if (isSaleRecalculationInProgress) return
    // Trigger Terminal Status request
    dispatch(requestTerminalStatusUpdate())

    dispatch(setIsTransitioningToPaymentScreenViaLsPay(true))
    trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Payment type selected', {
      amount: outstandingBalance,
      paymentTypeId: PAYMENT_TYPES.LSPAY,
      registerSaleId: saleId,
      registerId: registerManager.getActiveRegister().id,
      isQuickPay: true,
      quickPayFeatureFlag: getFeatureFromCache(
        'lspay_pay_with_quick_pay_experiment'
      ),
    })
  }

  useEffect(() => {
    if (
      isTransitioningToPaymentScreenViaLsPay &&
      !saleCalculationRunning &&
      !disableQuickPayButton &&
      !isSaleRecalculationInProgress
    ) {
      dispatch(setIsTransitioningToPaymentScreenViaLsPay(false))
      transitionToPaymentScreen()
    }
  }, [
    disableQuickPayButton,
    dispatch,
    isSaleRecalculationInProgress,
    isTransitioningToPaymentScreenViaLsPay,
    saleCalculationRunning,
    transitionToPaymentScreen,
  ])

  return (
    <div className="sale-action-bar" aria-label="payment button groups">
      {showQuickPayButton && (
        <Tooltip
          className="wr-tooltip"
          direction="center"
          message={
            <T
              _str="Payment amount must be at least {minAmount}"
              _context="tooltip message for lightspeed payments minimum amount"
              minAmount={`${currencySymbol}${minimumAmount.toFixed(2)}`}
              lineBreak={<br />}
            />
          }
          header={t('Minimum payment amount required', {
            _context: 'tooltip header for lightspeed payments minimum amount',
          })}
          canBeShown={isAmountBelowMinimum}
          icon="info-circle"
        >
          <button
            type="button"
            className="vd-btn vd-btn--supplementary vd-btn--jumbo vd-ml1"
            data-cy="quick-pay-btn"
            data-testid="quick-pay-btn"
            onClick={handleQuickPayClick}
            disabled={disableQuickPayButton || isSaleRecalculationInProgress}
          >
            {isLSPaySplitEntryEnabled ? (
              <LSPayCardPresentEntryIcon aria-hidden="true" focusable="false" />
            ) : (
              <Icon icon="fa-credit-card"></Icon>
            )}
          </button>
        </Tooltip>
      )}
      <SalePaymentButton />
    </div>
  )
}
